import React from "react";
import {
  Badge,
  Box,
  Button,
  Circle,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiHash,
  FiHome,
  FiMapPin,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { HeroTexture } from "../components/common/HeroTexture";
import { SejamSyncRequiredSection } from "../components/profile/SejamSyncRequiredSection";
import { SejamSyncModal } from "../components/SejamSyncModal";
import { auth } from "../services/api";
import {
  getIsPrivatePerson,
  getUserIdentifier,
  getUserIdentifierLabel,
  getUserPersonTypeLabel,
} from "../utils/userIdentity";

const MotionBox = motion(Box);

type UserInfoRecord = {
  firstName?: string;
  lastName?: string;
  fatherName?: string;
  gender?: string;
  birthDate?: string | null;
  placeOfBirth?: string;
  placeOfIssue?: string;
  seriShChar?: string;
  seriSh?: string;
  serial?: string;
  shNumber?: string;
  mobile?: string;
  address?: {
    province?: string;
    city?: string;
    postalCode?: string;
    fullAddress?: string;
    remnantAddress?: string;
    alley?: string;
    plaque?: string;
  };
  bankAccount?: {
    sheba?: string;
    accountNumber?: string;
    bankName?: string;
    bankId?: number | null;
    isDefault?: boolean;
    lastModified?: string | null;
  };
  updatedAt?: string;
};

type ProfilePayload = {
  _id: string;
  nationalId?: string;
  businessID?: string;
  fullName?: string;
  mobile?: string;
  isPrivatePerson?: boolean;
  isSejamVerified?: boolean;
  isTadbirVerified?: boolean;
  hasSejamRecord?: boolean;
  createdAt?: string;
  updatedAt?: string;
  role?: {
    label?: string;
    value?: string;
    isSuperAdmin?: boolean;
  } | null;
  userInfo?: UserInfoRecord | null;
};

type InfoField = {
  label: string;
  value: string;
  icon: any;
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "—";
  }

  return new Date(value).toLocaleDateString("fa-IR");
};

const formatGender = (value?: string) => {
  if (!value) {
    return "—";
  }

  if (value === "Male") {
    return "مرد";
  }

  if (value === "Female") {
    return "زن";
  }

  return value;
};

const valueOrDash = (value?: string | number | null) =>
  value === undefined || value === null || value === "" ? "—" : String(value);

const getInitials = (fullName?: string) => {
  if (!fullName) {
    return "ا";
  }

  return fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || "")
    .join("")
    .toUpperCase();
};

const DetailGrid = ({ fields }: { fields: InfoField[] }) => {
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.800", "white");
  const itemBg = useColorModeValue("whiteAlpha.900", "whiteAlpha.50");
  const itemBorder = useColorModeValue("blackAlpha.100", "whiteAlpha.160");
  const hoverBorder = useColorModeValue("brand.200", "whiteAlpha.220");
  const iconBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const iconColor = useColorModeValue("brand.500", "brand.200");

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
      {fields.map((field) => (
        <Box
          key={field.label}
          bg={itemBg}
          borderWidth="1px"
          borderColor={itemBorder}
          rounded="2xl"
          px={4}
          py={3.5}
          transition="all 0.2s ease"
          _hover={{
            transform: "translateY(-2px)",
            borderColor: hoverBorder,
          }}
        >
          <HStack align="flex-start" spacing={3}>
            <Circle size="34px" bg={iconBg} color={iconColor} flexShrink={0}>
              <Icon as={field.icon as any} boxSize={3.5} />
            </Circle>

            <VStack align="flex-start" spacing={1} flex="1" minW="0">
              <Text fontSize="xs" color={labelColor}>
                {field.label}
              </Text>
              <Text
                fontSize="sm"
                fontWeight="700"
                color={valueColor}
                wordBreak="break-word"
                lineHeight="1.9"
              >
                {field.value}
              </Text>
            </VStack>
          </HStack>
        </Box>
      ))}
    </SimpleGrid>
  );
};

const ProfileStat = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) => {
  const cardBg = useColorModeValue("whiteAlpha.900", "whiteAlpha.90");
  const borderColor = useColorModeValue("blackAlpha.100", "whiteAlpha.120");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueColor = useColorModeValue("gray.900", "white");
  const helperColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      rounded="2xl"
      p={4}
      minH="120px"
      backdropFilter="blur(10px)"
    >
      <VStack align="flex-start" spacing={2}>
        <Text fontSize="xs" color={labelColor}>
          {label}
        </Text>
        <Text
          fontSize={{ base: "2xl", md: "3xl" }}
          fontWeight="900"
          color={valueColor}
        >
          {value}
        </Text>
        <Text fontSize="sm" color={helperColor} lineHeight="1.9">
          {helper}
        </Text>
      </VStack>
    </Box>
  );
};

const SectionCard = ({
  title,
  subtitle,
  icon,
  fields,
  delay,
}: {
  title: string;
  subtitle: string;
  icon: any;
  fields: InfoField[];
  delay: number;
}) => {
  const cardBg = useColorModeValue(
    "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(250,250,251,0.98))",
    "linear-gradient(180deg, rgba(23,36,61,0.98), rgba(19,32,54,0.98))",
  );
  const borderColor = useColorModeValue("blackAlpha.100", "whiteAlpha.160");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");
  const iconBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const iconColor = useColorModeValue("brand.500", "brand.200");
  const chipBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const chipColor = useColorModeValue("brand.700", "brand.100");

  return (
    <MotionBox
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.42, delay, ease: "easeOut" }}
    >
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        rounded="3xl"
        overflow="hidden"
        h="100%"
        position="relative"
      >
        <Box
          position="absolute"
          top="0"
          insetInline="0"
          h="1px"
          bgGradient="linear(to-r, transparent, brand.300, transparent)"
        />

        <VStack align="stretch" spacing={5} p={{ base: 5, md: 6 }}>
          <HStack justify="space-between" align="flex-start" spacing={4}>
            <HStack spacing={3} align="center">
              <Circle size="50px" bg={iconBg} color={iconColor}>
                <Icon as={icon as any} boxSize={5} />
              </Circle>
              <VStack align="flex-start" spacing={0.5}>
                <Text fontSize="lg" fontWeight="900">
                  {title}
                </Text>
                <Text fontSize="sm" color={subtitleColor}>
                  {subtitle}
                </Text>
              </VStack>
            </HStack>

            <Badge bg={chipBg} color={chipColor} px={3} py={1} rounded="full">
              {fields.length} آیتم
            </Badge>
          </HStack>

          <DetailGrid fields={fields} />
        </VStack>
      </Box>
    </MotionBox>
  );
};

export const ProfilePage: React.FC = () => {
  const { data, isLoading, refetch } = useQuery(
    ["authMeProfile"],
    async () => {
      const response = await auth.getMe();
      return response.result as ProfilePayload;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [isSendingSyncOtp, setIsSendingSyncOtp] = React.useState(false);

  const pageBg = useColorModeValue(
    "linear-gradient(180deg, #f8fafc 0%, #fff7f7 45%, #ffffff 100%)",
    "linear-gradient(180deg, #0f1c31 0%, #132036 42%, #17243d 100%)",
  );
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,248,248,0.92) 35%, rgba(249,216,215,0.88) 100%)",
    "linear-gradient(135deg, rgba(23,36,61,0.96) 0%, rgba(19,32,54,0.98) 50%, rgba(138,9,5,0.28) 100%)",
  );
  const heroOverlay = useColorModeValue(
    "radial-gradient(circle at 12% 20%, rgba(255,255,255,0.95), transparent 30%), radial-gradient(circle at 85% 18%, rgba(249,168,166,0.42), transparent 34%), radial-gradient(circle at 78% 82%, rgba(255,194,0,0.14), transparent 22%)",
    "radial-gradient(circle at 18% 20%, rgba(255,255,255,0.08), transparent 26%), radial-gradient(circle at 86% 18%, rgba(249,168,166,0.16), transparent 32%), radial-gradient(circle at 78% 80%, rgba(255,194,0,0.1), transparent 20%)",
  );
  const heroText = useColorModeValue("gray.900", "white");
  const heroMuted = useColorModeValue("gray.600", "gray.300");
  const emptyCardBg = useColorModeValue("whiteAlpha.900", "whiteAlpha.80");
  const emptyBorder = useColorModeValue("blackAlpha.100", "whiteAlpha.200");
  const emptyMutedText = useColorModeValue("gray.500", "gray.400");
  const subtleBadgeBg = useColorModeValue("whiteAlpha.880", "whiteAlpha.140");
  const subtleBadgeColor = useColorModeValue("brand.700", "white");
  const avatarRing = useColorModeValue(
    "rgba(255,255,255,0.96)",
    "whiteAlpha.160",
  );

  const userInfo = data?.userInfo || {};
  const requiresSejamSync = !data?.hasSejamRecord;
  const isPrivatePerson = getIsPrivatePerson(data);
  const identifierValue = getUserIdentifier(data);
  const identifierLabel = getUserIdentifierLabel(data);
  const personTypeLabel = getUserPersonTypeLabel(data);

  const fullName =
    data?.fullName ||
    [userInfo.firstName, userInfo.lastName].filter(Boolean).join(" ") ||
    "کاربر امید";
  const phoneNumber = userInfo.mobile || data?.mobile;
  const sejamUpdatedAt = userInfo.updatedAt || data?.updatedAt;

  const personalFields: InfoField[] = isPrivatePerson
    ? [
        { label: "نام", value: valueOrDash(userInfo.firstName), icon: FiUser },
        {
          label: "نام خانوادگی",
          value: valueOrDash(userInfo.lastName),
          icon: FiUser,
        },
        {
          label: "نام پدر",
          value: valueOrDash(userInfo.fatherName),
          icon: FiUser,
        },
        {
          label: "جنسیت",
          value: formatGender(userInfo.gender),
          icon: FiUser,
        },
        {
          label: "تاریخ تولد",
          value: formatDate(userInfo.birthDate),
          icon: FiCalendar,
        },
        {
          label: "محل تولد",
          value: valueOrDash(userInfo.placeOfBirth),
          icon: FiMapPin,
        },
      ]
    : [
        { label: "نوع شخص", value: personTypeLabel, icon: FiUser },
        { label: "نام/عنوان", value: valueOrDash(fullName), icon: FiUser },
        {
          label: "شناسه هویتی",
          value: valueOrDash(identifierValue),
          icon: FiHash,
        },
      ];

  const identityFields: InfoField[] = [
    {
      label: identifierLabel,
      value: valueOrDash(identifierValue),
      icon: FiHash,
    },
    { label: "نوع شخص", value: personTypeLabel, icon: FiUser },
    ...(isPrivatePerson
      ? [
          {
            label: "شماره شناسنامه",
            value: valueOrDash(userInfo.shNumber),
            icon: FiHash,
          },
          {
            label: "محل صدور",
            value: valueOrDash(userInfo.placeOfIssue),
            icon: FiMapPin,
          },
          {
            label: "سری شناسنامه",
            value: valueOrDash(
              [userInfo.seriShChar, userInfo.seriSh].filter(Boolean).join(" "),
            ),
            icon: FiShield,
          },
          {
            label: "سریال شناسنامه",
            value: valueOrDash(userInfo.serial),
            icon: FiShield,
          },
        ]
      : []),
  ];

  const addressFields: InfoField[] = [
    {
      label: "استان",
      value: valueOrDash(userInfo.address?.province),
      icon: FiMapPin,
    },
    {
      label: "شهر",
      value: valueOrDash(userInfo.address?.city),
      icon: FiMapPin,
    },
    {
      label: "کد پستی",
      value: valueOrDash(userInfo.address?.postalCode),
      icon: FiHash,
    },
    {
      label: "آدرس کامل",
      value: valueOrDash(userInfo.address?.fullAddress),
      icon: FiHome,
    },
    {
      label: "کوچه",
      value: valueOrDash(userInfo.address?.alley),
      icon: FiHome,
    },
    {
      label: "پلاک",
      value: valueOrDash(userInfo.address?.plaque),
      icon: FiHome,
    },
  ];

  const bankingFields: InfoField[] = [
    {
      label: "نام بانک",
      value: valueOrDash(userInfo.bankAccount?.bankName),
      icon: FiCreditCard,
    },
    {
      label: "شماره حساب",
      value: valueOrDash(userInfo.bankAccount?.accountNumber),
      icon: FiHash,
    },
    {
      label: "شماره شبا",
      value: valueOrDash(userInfo.bankAccount?.sheba),
      icon: FiCreditCard,
    },
    {
      label: "حساب پیش فرض",
      value: userInfo.bankAccount?.isDefault ? "بله" : "خیر",
      icon: FiCheckCircle,
    },
    {
      label: "آخرین بروزرسانی بانکی",
      value: formatDate(userInfo.bankAccount?.lastModified),
      icon: FiClock,
    },
  ];

  const handleStartSejamSync = async () => {
    try {
      setIsSendingSyncOtp(true);
      await auth.requestSyncOtp();
      toast({
        title: "کد تایید ارسال شد",
        description: "کد تایید سجام به شماره موبایل ثبت‌شده شما ارسال شد",
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      onOpen();
    } catch (error: any) {
      toast({
        title: "خطا در ارسال کد",
        description:
          error.response?.data?.message || "خطا در ارسال کد تایید سجام",
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsSendingSyncOtp(false);
    }
  };

  if (isLoading) {
    return (
      <Flex minH="100dvh" justify="center" align="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color={heroMuted}>در حال دریافت اطلاعات پروفایل...</Text>
        </VStack>
      </Flex>
    );
  }

  if (!data) {
    return (
      <Box p="20px">
        <Box
          bg={emptyCardBg}
          borderWidth="1px"
          borderColor={emptyBorder}
          rounded="3xl"
          p={10}
          textAlign="center"
        >
          <Text fontWeight="700" mb={2}>
            اطلاعات پروفایل در دسترس نیست
          </Text>
          <Text color={emptyMutedText}>
            در حال حاضر داده‌ای برای نمایش دریافت نشد.
          </Text>
        </Box>
      </Box>
    );
  }

  if (requiresSejamSync) {
    return (
      <SejamSyncRequiredSection
        identifier={identifierValue}
        identifierLabel={identifierLabel}
        description={
          requiresSejamSync
            ? "برای ادامه سرمایه‌گذاری، لازم است یک‌بار دیگر اطلاعات سجام شما دریافت و در سامانه ذخیره شود تا ثبت‌نام تدبیر تکمیل شود."
            : undefined
        }
        onSynced={async () => {
          await refetch();
        }}
      />
    );
  }

  return (
    <Box h="100%" overflowY="auto">
      <VStack align="stretch" spacing={6}>
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <Box
            bg={heroBg}
            rounded={{ base: "3xl", xl: "4xl" }}
            px={{ base: 5, md: 7, xl: 8 }}
            py={{ base: 6, md: 7, xl: 8 }}
            color={heroText}
            overflow="hidden"
            position="relative"
            borderWidth="1px"
            borderColor={emptyBorder}
            _before={{
              content: '""',
              position: "absolute",
              inset: 0,
              background: heroOverlay,
              zIndex: 0,
            }}
          >
            <HeroTexture />

            <Stack
              position="relative"
              spacing={6}
              direction={{ base: "column", xl: "row" }}
              align={{ base: "stretch", xl: "center" }}
              justify="space-between"
              mb={5}
            >
              <Stack spacing={3} flex="1">
                <HStack spacing={2} flexWrap="wrap">
                  <Badge
                    bg={data.isSejamVerified ? "green.600" : "orange.500"}
                    color="white"
                    px={3}
                    py={1}
                    rounded="full"
                  >
                    <HStack spacing={1}>
                      <Icon
                        as={
                          (data.isSejamVerified
                            ? FiCheckCircle
                            : FiClock) as any
                        }
                        boxSize={3.5}
                      />
                      <Text>
                        {data.isSejamVerified
                          ? "سجام تایید شده"
                          : "سجام نیازمند بروزرسانی"}
                      </Text>
                    </HStack>
                  </Badge>

                  <Badge px={3} py={1} rounded="full">
                    نقش: {valueOrDash(data.role?.label || data.role?.value)}
                  </Badge>
                  <Badge px={3} py={1} rounded="full">
                    {personTypeLabel}
                  </Badge>
                </HStack>
                <HStack spacing={4} align="center">
                  <Circle
                    size={{ base: "72px", md: "84px" }}
                    bg={subtleBadgeBg}
                    borderWidth="1px"
                    borderColor={avatarRing}
                    fontSize={{ base: "xl", md: "2xl" }}
                    fontWeight="900"
                    color={subtleBadgeColor}
                    flexShrink={0}
                  >
                    {getInitials(fullName)}
                  </Circle>

                  <VStack align="flex-start" spacing={2}>
                    <VStack align="flex-start" spacing={1}>
                      <Text
                        fontSize={{ base: "2xl", md: "4xl" }}
                        fontWeight="950"
                      >
                        {fullName}
                      </Text>
                      <Text
                        color={heroMuted}
                        fontSize={{ base: "sm", md: "md" }}
                        lineHeight="2"
                      >
                        نمایی یکپارچه از اطلاعات هویتی، بانکی و وضعیت همگام‌سازی
                        حساب شما در داشبورد.
                      </Text>
                    </VStack>
                    <Button
                      size="md"
                      borderRadius="full"
                      onClick={handleStartSejamSync}
                      isLoading={isSendingSyncOtp}
                      loadingText="در حال ارسال کد..."
                    >
                      بروزرسانی اطلاعات سجام
                    </Button>
                  </VStack>
                </HStack>
              </Stack>
            </Stack>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
              <ProfileStat
                label="آخرین همگام‌سازی"
                value={formatDate(sejamUpdatedAt)}
                helper="این تاریخ آخرین زمان ثبت اطلاعات سجام در پروفایل شماست"
              />
              <ProfileStat
                label="شماره همراه"
                value={valueOrDash(phoneNumber)}
                helper="شماره ثبت‌شده برای اطلاع‌رسانی و دریافت کدهای تایید"
              />
              <ProfileStat
                label="بانک پیش فرض"
                value={valueOrDash(userInfo.bankAccount?.bankName)}
                helper="حساب منتخب برای عملیات بانکی و تراکنش‌های مرتبط"
              />
              <ProfileStat
                label={identifierLabel}
                value={valueOrDash(identifierValue)}
                helper="شناسه اصلی ثبت‌شده برای حساب کاربری شما"
              />
            </SimpleGrid>
          </Box>
        </MotionBox>

        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={5}>
          <SectionCard
            title="اطلاعات شخصی"
            subtitle="مشخصات اصلی فردی و اطلاعات پایه"
            icon={FiUser}
            fields={personalFields}
            delay={0.06}
          />
          <SectionCard
            title="اسناد هویتی"
            subtitle="مشخصات شناسنامه‌ای و اطلاعات شناسایی"
            icon={FiShield}
            fields={identityFields}
            delay={0.12}
          />
          <SectionCard
            title="نشانی"
            subtitle="آدرس سکونت و داده‌های مکانی ثبت‌شده"
            icon={FiHome}
            fields={addressFields}
            delay={0.18}
          />
          <SectionCard
            title="اطلاعات بانکی"
            subtitle="جزئیات حساب بانکی و وضعیت حساب پیش فرض"
            icon={FiCreditCard}
            fields={bankingFields}
            delay={0.24}
          />
        </SimpleGrid>
      </VStack>

      <SejamSyncModal
        isOpen={isOpen}
        onClose={onClose}
        onSynced={async () => {
          await refetch();
        }}
      />
    </Box>
  );
};
